if (!customElements.get("pf-pilot-product-info")) {
  customElements.define(
    "pf-pilot-product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSealSubscribeOverride();
        this.redirectSoldOutLandingToAvailableCondition();
        this.dispatchEvent(new CustomEvent("product-info:loaded", { bubbles: true }));
      }

      // Landing correction: an inbound URL pinned to a sold-out variant lands on
      // the first available rendered Condition for the same model, color, and
      // storage. Runs once per page load so shopper-triggered swaps keep the
      // existing in-place selection behavior.
      redirectSoldOutLandingToAvailableCondition() {
        if (window.pfPilotSoldOutLandingChecked) return;
        window.pfPilotSoldOutLandingChecked = true;

        const currentUrl = new URL(window.location.href);
        const hasPinnedSelection =
          currentUrl.searchParams.has("variant") || currentUrl.searchParams.has("option_values");
        if (!hasPinnedSelection) return;

        const selectedVariant = this.getSelectedVariant(this);
        if (!selectedVariant || selectedVariant.available !== false) return;

        const targetVariantId = this.findAvailableConditionVariantId(selectedVariant);
        if (!targetVariantId) return;

        currentUrl.searchParams.delete("option_values");
        currentUrl.searchParams.set("variant", targetVariantId);
        window.location.replace(`${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
      }

      // First available rendered pill inside the option actually named Condition,
      // in rendered order. Pills that map to another product (combined-listing
      // inputs carry data-product-url) are never landing fallbacks.
      findAvailableConditionVariantId(selectedVariant) {
        const fieldsets = Array.from(
          this.querySelectorAll('variant-selects:not([data-upsell-isolated="true"]) fieldset')
        );
        const conditionFieldset = fieldsets.find((fieldset) => {
          const name =
            fieldset.dataset.optionName ||
            fieldset.dataset.option ||
            fieldset.querySelector("legend, label")?.textContent?.split(":")[0];
          return this.normalizeOptionName(name).includes("condition");
        });
        if (!conditionFieldset) return null;

        const productPath = this.normalizeProductPath(this.dataset.url);
        const candidate = Array.from(
          conditionFieldset.querySelectorAll('input[type="radio"]')
        ).find((input) => {
          if (input.classList.contains("disabled")) return false;
          if (!input.dataset.pfTargetVariantId) return false;
          const candidatePath = input.dataset.productUrl
            ? this.normalizeProductPath(input.dataset.productUrl)
            : productPath;
          return candidatePath === productPath;
        });
        if (!candidate) return null;

        const targetVariantId = candidate.dataset.pfTargetVariantId;
        return String(targetVariantId) === String(selectedVariant.id) ? null : targetVariantId;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initSealSubscribeOverride() {
        const subscribeOnlyUsingSeal = this.dataset.subscribeOnlyUsingSeal === "true";

        if (!subscribeOnlyUsingSeal) return;

        const productInfoWrapper = this.querySelector(".product__info-wrapper");
        if (!productInfoWrapper) {
          console.error("Product info wrapper not found");
          return;
        }

        // Wait for the seal subscribe button to be loaded by the 3rd party app
        this.waitForSealSubscribeButton(productInfoWrapper);
      }

      waitForSealSubscribeButton(productInfoWrapper) {
        // First, try immediate check
        const immediateCheck = this.checkForSealButton(productInfoWrapper);
        if (immediateCheck) return;

        // If not found immediately, set up MutationObserver to watch for DOM changes
        this.setupSealButtonObserver(productInfoWrapper);

        // Also keep the polling as a fallback, but with longer timeout
        this.startSealButtonPolling(productInfoWrapper);
      }

      checkForSealButton(productInfoWrapper) {
        const sealSubscribeContainer = productInfoWrapper.querySelector(".sealsubs-subscribe-now");
        const sealSubscribeButton = sealSubscribeContainer?.querySelector(
          ".sealsubs-subscribe-now-button"
        );

        if (sealSubscribeButton) {
          this.enableSealButtons(productInfoWrapper, sealSubscribeButton);
          return true;
        }
        return false;
      }

      setupSealButtonObserver(productInfoWrapper) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === "childList" || mutation.type === "subtree") {
              const found = this.checkForSealButton(productInfoWrapper);
              if (found) {
                observer.disconnect();
              }
            }
          });
        });

        // Start observing the entire product info wrapper for changes
        observer.observe(productInfoWrapper, {
          childList: true,
          subtree: true,
          attributes: false,
        });
      }

      startSealButtonPolling(productInfoWrapper) {
        const maxAttempts = 100; // Increased to 10 seconds
        let attempts = 0;

        const pollForSealButton = () => {
          attempts++;

          const found = this.checkForSealButton(productInfoWrapper);
          if (found) return;

          if (attempts < maxAttempts) {
            setTimeout(pollForSealButton, 100);
          } else {
            console.warn("Seal subscribe button not found after maximum polling attempts");
          }
        };

        // Start polling
        setTimeout(pollForSealButton, 100);
      }

      enableSealButtons(productInfoWrapper, sealSubscribeButton) {
        // Enable Seal buttons and add click handlers
        const sealButtons = productInfoWrapper.querySelectorAll(".product-form__submit-seal");

        sealButtons.forEach((button) => {
          button.disabled = false;
          button.classList.remove("seal-waiting");

          // Add click handler to trigger Seal subscribe
          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Enable loading spinner
            this.enableSealButtonLoading(button);

            // Click the seal subscribe button instead
            sealSubscribeButton.click();
          });
        });
      }

      enableSealButtonLoading(button) {
        // Disable the button and show loading spinner
        button.disabled = true;
        button.classList.add("loading");

        // Show the loading spinner
        const spinner = button.querySelector(".loading__spinner");
        if (spinner) {
          spinner.classList.remove("hidden");
        }

        // Make the button text transparent instead of hiding it
        const span = button.querySelector("span");
        if (span) {
          span.style.opacity = "0";
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) => element.classList.add("scroll-trigger--cancel"))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) {
          return;
        }

        this.resetProductFormState();

        const candidateProductUrl =
          target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct =
          this.normalizeProductPath(this.dataset.url) !==
          this.normalizeProductPath(candidateProductUrl);
        const requestProductUrl = this.buildPilotTemplateUrl(candidateProductUrl);
        const historyProductUrl = this.buildHistoryProductUrl(
          candidateProductUrl,
          shouldSwapProduct
        );
        this.pendingRequestUrl = requestProductUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === "true" && shouldSwapProduct;

        // Check if redirect on variant change is enabled
        const redirectOnVariantChange = this.dataset.redirectOnVariantChange === "true";

        if (redirectOnVariantChange) {
          // Redirect for the selected options, but land on an in-stock condition
          this.redirectToInStockVariant(historyProductUrl, selectedOptionValues);
          return;
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            requestProductUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(
                historyProductUrl,
                shouldFetchFullPage,
                this.getChangedOptionIndex(event.target)
              )
            : this.handleUpdateProductInfo(historyProductUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage, changedOptionIndex = -1) {
        return (html) => {
          this.productModal?.remove();

          const selector = "pf-pilot-product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("pf-pilot-product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
          const liveProductInfo = document.querySelector(selector);
          const postSwapVariant = liveProductInfo ? this.getSelectedVariant(liveProductInfo) : null;
          const variantForPublish = postSwapVariant || variant;

          if (variantForPublish) {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: liveProductInfo?.dataset?.section || this.sectionId,
                html: null,
                variant: variantForPublish,
              },
            });
          }

          // Land on in-stock options: when the swapped-in product preselects a
          // sold-out variant, switch to the closest in-stock one — or, if this
          // whole product is sold out, hop to the closest in-stock sibling
          if (variantForPublish && variantForPublish.available === false && liveProductInfo) {
            this.selectInStockAlternative(
              productUrl,
              variantForPublish,
              liveProductInfo,
              changedOptionIndex
            );
          }
        };
      }

      // Rate-limits the automatic stock corrections so cross-product hops can
      // never ping-pong: at most 4 corrective selections per 6-second window
      canAutoCorrectStock() {
        const now = Date.now();
        const state = window.pfPilotStockCascade;
        if (!state || now - state.ts > 6000) {
          window.pfPilotStockCascade = { ts: now, count: 1 };
          return true;
        }
        state.count += 1;
        return state.count <= 4;
      }

      getChangedOptionIndex(target) {
        const fieldset = target?.closest?.("fieldset");
        if (!fieldset || !this.variantSelectors) return -1;
        return Array.from(this.variantSelectors.querySelectorAll("fieldset")).indexOf(fieldset);
      }

      normalizeOptionName(value) {
        return String(value || "")
          .replace(/^\s*select\s+/i, "")
          .replace(/:\s*$/, "")
          .trim()
          .toLowerCase();
      }

      getConditionOptionIndex(product, productInfoNode = this) {
        const productOptions = Array.isArray(product?.options) ? product.options : [];
        const productOptionIndex = productOptions.findIndex((option) => {
          const name = typeof option === "string" ? option : option?.name;
          return this.normalizeOptionName(name).includes("condition");
        });
        if (productOptionIndex >= 0) return productOptionIndex;

        const declaredPosition = Number(
          document.querySelector("[data-pf-condition-option-position]")?.dataset
            ?.pfConditionOptionPosition
        );
        if (declaredPosition > 0) return declaredPosition - 1;

        const fieldsets = Array.from(
          productInfoNode?.querySelectorAll?.(
            'variant-selects:not([data-upsell-isolated="true"]) fieldset'
          ) || []
        );
        return fieldsets.findIndex((fieldset) => {
          const name =
            fieldset.dataset.optionName ||
            fieldset.dataset.option ||
            fieldset.querySelector("legend, label")?.textContent?.split(":")[0];
          return this.normalizeOptionName(name).includes("condition");
        });
      }

      findInStockVariant(product, selectedOptions, conditionOptionIndex, changedOptionIndex = -1) {
        const ignoredOptionIndex =
          conditionOptionIndex >= 0 ? conditionOptionIndex : changedOptionIndex;
        const matchesPreservedOptions = (variant) =>
          variant.options.every(
            (option, index) => index === ignoredOptionIndex || option === selectedOptions[index]
          );

        return (
          product.variants.find(
            (variant) => variant.available && matchesPreservedOptions(variant)
          ) || product.variants.find((variant) => variant.available)
        );
      }

      // Picks the first in-stock variant that preserves every option except the
      // option actually named Condition, then falls back to any in-stock variant.
      selectInStockAlternative(
        productUrl,
        selectedVariant,
        productInfoNode,
        changedOptionIndex = -1
      ) {
        if (!this.canAutoCorrectStock()) return;
        const basePath = productUrl.split("?")[0];
        fetch(`${basePath}.js`)
          .then((response) =>
            response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))
          )
          .then((product) => {
            const selectedOptions = selectedVariant.options || [];
            const conditionOptionIndex = this.getConditionOptionIndex(product, productInfoNode);
            const inStock = this.findInStockVariant(
              product,
              selectedOptions,
              conditionOptionIndex,
              changedOptionIndex
            );
            if (!inStock) {
              this.selectInStockSibling(productInfoNode, changedOptionIndex);
              return;
            }
            if (inStock.id === selectedVariant.id) return;

            const optionIndex = inStock.options.findIndex(
              (option, index) => option !== selectedOptions[index]
            );
            const fieldsets = productInfoNode.querySelectorAll(
              'variant-selects:not([data-upsell-isolated="true"]) fieldset'
            );
            const escapedValue = inStock.options[optionIndex]?.replace(/"/g, '\\"');
            const input = fieldsets[optionIndex]?.querySelector(
              `input[type="radio"][value="${escapedValue}"]`
            );
            if (input && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event("change", { bubbles: true }));
            }
          })
          .catch(() => {});
      }

      // Walks the picker options from last to first — skipping the option the
      // shopper just picked so their choice is honored — and selects the first
      // sibling product (combined-listing inputs carry data-product-url) that has
      // any variant in stock. The normal swap flow then lands it on an in-stock
      // available variant. Stays put when no sibling has stock.
      selectInStockSibling(productInfoNode, changedOptionIndex) {
        const fieldsets = Array.from(
          productInfoNode.querySelectorAll(
            'variant-selects:not([data-upsell-isolated="true"]) fieldset'
          )
        );

        const tryFieldset = (fieldsetIndex) => {
          if (fieldsetIndex < 0) return;
          if (fieldsetIndex === changedOptionIndex) {
            tryFieldset(fieldsetIndex - 1);
            return;
          }
          const candidates = Array.from(
            fieldsets[fieldsetIndex]?.querySelectorAll('input[type="radio"]:not(:checked)') || []
          ).filter((input) => input.dataset.productUrl);

          const tryCandidate = (candidateIndex) => {
            if (candidateIndex >= candidates.length) {
              tryFieldset(fieldsetIndex - 1);
              return;
            }
            const candidate = candidates[candidateIndex];
            fetch(`${candidate.dataset.productUrl.split("?")[0]}.js`)
              .then((response) =>
                response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))
              )
              .then((product) => {
                if (product.variants.some((variant) => variant.available)) {
                  candidate.checked = true;
                  candidate.dispatchEvent(new Event("change", { bubbles: true }));
                } else {
                  tryCandidate(candidateIndex + 1);
                }
              })
              .catch(() => tryCandidate(candidateIndex + 1));
          };
          tryCandidate(0);
        };

        tryFieldset(fieldsets.length - 1);
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, "text/html");
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            const focusEl = document.querySelector(`#${targetId}`);
            focusEl?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              return;
            }
            console.error(error);
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          'variant-selects:not([data-upsell-isolated="true"]) [data-selected-variant]'
        )?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      normalizeProductPath(url) {
        try {
          return new URL(url, window.location.origin).pathname.replace(/\/$/, "");
        } catch {
          return "";
        }
      }

      // Every pilot template now renders the quick protection panel, so the panel markup says nothing
      // about the template. Only a shopper already on ?view=protection-quick (old Shoplift or bookmarked
      // links) or on a Shoplift test (?view=protection-first, ?view=guided-options, ?view=trade-in)
      // stays there; everyone else fetches through the default pilot view, which has no trade-in.
      getPilotView() {
        const view = new URL(window.location.href).searchParams.get("view");
        if (
          view === "protection-quick" ||
          view === "protection-first" ||
          view === "guided-options" ||
          view === "trade-in"
        )
          return view;
        return "pf-scroll-pilot";
      }

      isPilotView(view) {
        return (
          view === "pf-scroll-pilot" ||
          view === "protection-quick" ||
          view === "protection-first" ||
          view === "guided-options" ||
          view === "trade-in"
        );
      }

      buildPilotTemplateUrl(url) {
        const targetUrl = new URL(url, window.location.origin);
        targetUrl.searchParams.set("view", this.getPilotView());
        const previewThemeId = new URL(window.location.href).searchParams.get("preview_theme_id");
        if (previewThemeId) {
          targetUrl.searchParams.set("preview_theme_id", previewThemeId);
        }
        targetUrl.hash = "";
        return `${targetUrl.pathname}${targetUrl.search}`;
      }

      buildHistoryProductUrl(url, shouldSwapProduct) {
        const targetUrl = new URL(url, window.location.origin);
        const currentUrl = new URL(window.location.href);
        const previewThemeId = currentUrl.searchParams.get("preview_theme_id");
        if (previewThemeId) {
          targetUrl.searchParams.set("preview_theme_id", previewThemeId);
        }
        if (shouldSwapProduct || this.isPilotView(currentUrl.searchParams.get("view"))) {
          targetUrl.searchParams.set("view", this.getPilotView());
        } else {
          targetUrl.searchParams.delete("view");
        }
        targetUrl.searchParams.delete("section_id");
        targetUrl.searchParams.delete("option_values");
        targetUrl.searchParams.delete("variant");
        targetUrl.hash = "";
        return `${targetUrl.pathname}${targetUrl.search}`;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.origin);
        if (shouldFetchFullPage) {
          requestUrl.searchParams.delete("section_id");
        } else {
          requestUrl.searchParams.set("section_id", this.sectionId);
        }
        if (optionValues.length) {
          requestUrl.searchParams.set("option_values", optionValues.join(","));
        } else {
          requestUrl.searchParams.delete("option_values");
        }
        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      buildRedirectUrl(url, optionValues) {
        const redirectUrl = new URL(url, window.location.origin);
        if (optionValues.length) {
          redirectUrl.searchParams.set("option_values", optionValues.join(","));
        } else {
          redirectUrl.searchParams.delete("option_values");
        }
        return `${redirectUrl.pathname}${redirectUrl.search}`;
      }

      get selectedOptionNames() {
        return Array.from(
          this.variantSelectors?.querySelectorAll(
            "select option[selected], fieldset input:checked"
          ) || []
        ).map(({ value }) => value);
      }

      // Redirect to the target product for the selected options, correcting the
      // landing selection when it would be sold out or nonexistent there while
      // preserving every option except the option actually named Condition.
      redirectToInStockVariant(productUrl, selectedOptionValues) {
        const basePath = productUrl.split("?")[0];
        const fallbackUrl = this.buildRedirectUrl(productUrl, selectedOptionValues);
        const selectedOptions = this.selectedOptionNames;
        const abort = new AbortController();
        const timer = setTimeout(() => abort.abort(), 2000);

        fetch(`${basePath}.js`, { signal: abort.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((product) => {
            const exact = product.variants.find(
              (variant) =>
                variant.options.length === selectedOptions.length &&
                variant.options.every((option, index) => option === selectedOptions[index])
            );
            if (exact && exact.available) return fallbackUrl;

            const conditionOptionIndex = this.getConditionOptionIndex(product, this);
            const inStock = this.findInStockVariant(product, selectedOptions, conditionOptionIndex);
            return inStock ? `${basePath}?variant=${inStock.id}` : fallbackUrl;
          })
          .catch(() => fallbackUrl)
          .then((url) => {
            clearTimeout(timer);
            window.location.href = url;
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector(
          'variant-selects:not([data-upsell-isolated="true"])'
        );
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);

          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          const updateAllPrices = () => {
            updateSourceFromDestination("price");

            // Search document-wide: scroll templates render price blocks (e.g. the
            // intro price) outside this element but within the same section
            const priceElements = document.querySelectorAll(
              `[id^="price-${this.dataset.section}"]`
            );
            priceElements.forEach((priceElement) => {
              const priceId = priceElement.id;
              const sourcePrice = html.getElementById(
                priceId.replace(this.dataset.section, this.sectionId)
              );
              if (sourcePrice) {
                priceElement.innerHTML = sourcePrice.innerHTML;
                priceElement.classList.toggle("hidden", sourcePrice.classList.contains("hidden"));
              }
            });
          };

          updateAllPrices();
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove("hidden");
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove("hidden");

          this.querySelectorAll("pf-pilot-product-form").forEach((productForm) => {
            productForm.toggleSubmitButton(
              html
                .getElementById(`ProductSubmitButton-${this.sectionId}`)
                ?.hasAttribute("disabled") ?? true,
              window.variantStrings.soldOut
            );
          });

          this.querySelectorAll(".price__badge-sold-out").forEach((badge) => {
            badge.classList.toggle("hidden", variant?.available);
          });
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const destination = new URL(url, window.location.origin);
        const currentUrl = new URL(window.location.href);
        const currentView = currentUrl.searchParams.get("view");
        const previewThemeId = currentUrl.searchParams.get("preview_theme_id");
        if (this.isPilotView(currentView)) {
          destination.searchParams.set("view", currentView);
        }
        if (previewThemeId) {
          destination.searchParams.set("preview_theme_id", previewThemeId);
        }
        if (variantId) {
          destination.searchParams.set("variant", variantId);
        } else {
          destination.searchParams.delete("variant");
        }
        const storefrontUrl = `${destination.pathname}${destination.search}${destination.hash}`;
        this.querySelector("share-button")?.updateUrl(`${window.shopUrl}${storefrontUrl}`);

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState({}, "", storefrontUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(".quantity__rules-cart .loading__spinner").classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(".quantity__rules-cart .loading__spinner").classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = [".quantity__input", ".quantity__rules", ".quantity__label"];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = ["data-cart-quantity", "data-min", "data-max", "step"];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector("pf-pilot-product-form");
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects:not([data-upsell-isolated="true"])');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// cache-bust 2026-07-30
